import RAPIER from '@dimforge/rapier3d-compat';
import type { RenderoniEngine } from 'renderoni';
import type { VehicleStats } from './upgrades';

// Collision groups: high 16 bits = membership, low 16 bits = filter.
const GROUP_TERRAIN = 0x0001;
const GROUP_BUGGY = 0x0002;
export const TERRAIN_COLLISION_GROUPS = (GROUP_TERRAIN << 16) | GROUP_BUGGY;
const BUGGY_COLLISION_GROUPS = (GROUP_BUGGY << 16) | GROUP_TERRAIN;
/** Wheel ray-casts only see terrain. */
const WHEEL_RAY_GROUPS = (GROUP_BUGGY << 16) | GROUP_TERRAIN;

/** Contact separation (m) below which a collider counts as touching. */
const TOUCH_DISTANCE = 0.03;

export interface BuggyReadout {
  x: number;
  y: number;
  angle: number;
  forwardSpeed: number;
  angularVelocityZ: number;
  wheelGrounded: [boolean, boolean];
  helmetHit: boolean;
}

export interface WheelPose {
  /** Wheel centre in chassis space. */
  x: number;
  y: number;
  /** Accumulated spin angle (rad). */
  spin: number;
}

/**
 * A two-wheel buggy: one plane-locked chassis body driven by Rapier's DynamicRayCastVehicleController.
 * Wheels are suspension ray-casts, not bodies, so nothing can drift out of the play plane.
 * (A jointed-wheel rig was tried first; its hubs drifted sideways after a few hundred metres —
 * see artifacts/game-progress.md.) Small bump-stop spheres at the wheel positions stop steep
 * faces from passing through a fully compressed wheel.
 */
export class Buggy {
  readonly chassis: RAPIER.RigidBody;
  readonly helmet: RAPIER.Collider;
  readonly vehicle: RAPIER.DynamicRayCastVehicleController;
  private readonly inertiaZ: number;
  private readonly mass: number;

  constructor(
    private readonly engine: RenderoniEngine,
    spawnX: number,
    groundY: number,
    private readonly stats: VehicleStats,
  ) {
    const world = engine.native.world;
    const v = stats.vehicle;
    const c = v.chassis;
    const w = v.wheel;
    const s = v.suspension;

    this.chassis = world.createRigidBody(
      RAPIER.RigidBodyDesc.dynamic()
        .setTranslation(spawnX, groundY + c.spawnClearance, 0)
        .enabledTranslations(true, true, false)
        .enabledRotations(false, false, true)
        .setCcdEnabled(true),
    );
    world.createCollider(
      RAPIER.ColliderDesc.cuboid(c.halfLength, c.halfHeight, c.halfDepth)
        .setDensity(c.density)
        .setFriction(c.friction)
        .setCollisionGroups(BUGGY_COLLISION_GROUPS),
      this.chassis,
    );
    this.helmet = world.createCollider(
      RAPIER.ColliderDesc.ball(v.helmet.radius)
        .setTranslation(v.helmet.offset.x, v.helmet.offset.y, 0)
        .setDensity(0)
        .setCollisionGroups(BUGGY_COLLISION_GROUPS),
      this.chassis,
    );
    for (const ax of w.axleX) {
      world.createCollider(
        RAPIER.ColliderDesc.ball(w.radius * s.bumpStopRadiusScale)
          .setTranslation(ax, w.axleY + stats.suspensionTravel, 0)
          .setDensity(0)
          .setFriction(s.bumpStopFriction)
          .setCollisionGroups(BUGGY_COLLISION_GROUPS),
        this.chassis,
      );
    }

    this.vehicle = world.createVehicleController(this.chassis);
    for (const ax of w.axleX) {
      this.vehicle.addWheel(
        { x: ax, y: w.axleY + s.restLength, z: 0 },
        { x: 0, y: -1, z: 0 },
        { x: 0, y: 0, z: 1 },
        s.restLength,
        w.radius,
      );
      const i = this.vehicle.numWheels() - 1;
      this.vehicle.setWheelMaxSuspensionTravel(i, stats.suspensionTravel);
      this.vehicle.setWheelSuspensionStiffness(i, s.stiffness);
      this.vehicle.setWheelSuspensionCompression(i, stats.suspensionCompression);
      this.vehicle.setWheelSuspensionRelaxation(i, stats.suspensionRelaxation);
      this.vehicle.setWheelMaxSuspensionForce(i, s.maxForce);
      this.vehicle.setWheelFrictionSlip(i, stats.frictionSlip);
    }

    engine.add({ id: 'buggy', tags: ['buggy'], native: { rapier: { body: this.chassis } } });
    this.mass = this.chassis.mass();
    this.inertiaZ = this.chassis.principalInertia().z;
  }

  /** Applies one tick of driver input. `powered` is false when the tank is empty. */
  applyControls(throttle: number, powered: boolean, grounded: boolean, dt: number): void {
    const d = this.stats.vehicle.drive;
    const speed = this.chassis.linvel().x;
    let engineForce = 0;
    let brake = 0;
    if (throttle > 0 && powered) {
      // Force tapers to zero at top speed (the vehicle controller has no speed limit of its own).
      const taper = Math.max(0, 1 - speed / this.stats.topSpeed);
      engineForce = this.stats.acceleration * this.mass * throttle * taper;
    } else if (throttle < 0) {
      if (speed > d.reverseBelowSpeed || !powered) brake = d.brake * -throttle;
      else if (speed > -d.reverseTopSpeed) engineForce = -this.stats.acceleration * 0.6 * this.mass * -throttle;
    }
    for (let i = 0; i < 2; i++) {
      // All-wheel drive keeps steep climbs drivable.
      this.vehicle.setWheelEngineForce(i, engineForce / 2);
      this.vehicle.setWheelBrake(i, brake);
    }

    if (!grounded && throttle !== 0) {
      // Air control: gas pitches the nose up (counter-clockwise about +Z), brake pitches it down.
      const a = this.stats.vehicle.air;
      const spin = this.chassis.angvel().z;
      const wantsMore = throttle > 0 ? spin < a.maxPitchRate : spin > -a.maxPitchRate;
      if (wantsMore) this.chassis.applyTorqueImpulse({ x: 0, y: 0, z: a.pitchAccel * this.inertiaZ * throttle * dt }, true);
    }
  }

  /** Suspension ray-casts and wheel forces for this tick. Must run before the physics step. */
  updateVehicle(dt: number): void {
    this.enforcePlane();
    this.vehicle.updateVehicle(dt, undefined, WHEEL_RAY_GROUPS);
  }

  /**
   * Projects the chassis back onto the XY plane: z = 0, rotation about Z only, no out-of-plane velocity.
   * Rapier's rotation locks alone let roll build up under the vehicle controller's impulses
   * (≈1e-6 rad after 100 m, 50° after 390 m in headless tests), so the plane is enforced every tick.
   */
  private enforcePlane(): void {
    const c = this.chassis;
    const t = c.translation();
    const q = c.rotation();
    const v = c.linvel();
    const w = c.angvel();
    if (t.z === 0 && q.x === 0 && q.y === 0 && v.z === 0 && w.x === 0 && w.y === 0) return;
    const n = Math.hypot(q.z, q.w) || 1;
    c.setTranslation({ x: t.x, y: t.y, z: 0 }, false);
    c.setRotation({ x: 0, y: 0, z: q.z / n, w: q.w / n }, false);
    c.setLinvel({ x: v.x, y: v.y, z: 0 }, false);
    c.setAngvel({ x: 0, y: 0, z: w.z }, false);
  }

  read(): BuggyReadout {
    const world = this.engine.native.world;
    const t = this.chassis.translation();
    const r = this.chassis.rotation();
    // Rotation is about Z only, so the angle is 2·atan2(z, w). Presentation only; rules integrate angvel.
    const angle = 2 * Math.atan2(r.z, r.w);
    return {
      x: t.x,
      y: t.y,
      angle,
      forwardSpeed: this.chassis.linvel().x,
      angularVelocityZ: this.chassis.angvel().z,
      wheelGrounded: [this.vehicle.wheelIsInContact(0), this.vehicle.wheelIsInContact(1)],
      helmetHit: this.touching(world, this.helmet),
    };
  }

  /** Wheel centre (chassis space) and spin, for rendering. */
  wheelPose(i: number, out: WheelPose): WheelPose {
    const v = this.stats.vehicle;
    const length = this.vehicle.wheelSuspensionLength(i) ?? v.suspension.restLength;
    out.x = v.wheel.axleX[i];
    out.y = v.wheel.axleY + v.suspension.restLength - length;
    out.spin = this.vehicle.wheelRotation(i) ?? 0;
    return out;
  }

  /**
   * Places the buggy at a pose and clears its velocities.
   * Used by test hooks and tests; gameplay never teleports.
   */
  teleport(x: number, y: number, angle: number): void {
    const half = angle / 2;
    this.chassis.setTranslation({ x, y, z: 0 }, true);
    this.chassis.setRotation({ x: 0, y: 0, z: Math.sin(half), w: Math.cos(half) }, true);
    this.chassis.setLinvel({ x: 0, y: 0, z: 0 }, true);
    this.chassis.setAngvel({ x: 0, y: 0, z: 0 }, true);
  }

  /** Disables the drive (used when the run ends so the wreck just coasts). */
  cutPower(): void {
    for (let i = 0; i < 2; i++) {
      this.vehicle.setWheelEngineForce(i, 0);
      this.vehicle.setWheelBrake(i, 0);
    }
  }

  /** Must be called before the chassis entity is removed. */
  dispose(): void {
    this.engine.native.world.removeVehicleController(this.vehicle);
  }

  /** True when the collider has a real contact (not just a speculative one inside the prediction distance). */
  private touching(world: RAPIER.World, collider: RAPIER.Collider): boolean {
    let hit = false;
    world.contactPairsWith(collider, (other) => {
      if (hit) return;
      world.contactPair(collider, other, (manifold) => {
        for (let i = 0; i < manifold.numContacts() && !hit; i++) {
          if (manifold.contactDist(i) <= TOUCH_DISTANCE) hit = true;
        }
      });
    });
    return hit;
  }
}
